// Pure card helpers for the CP delivery-routes (touchpoints) screen.
//
// Extracted verbatim out of index.js so the identity + chip-visibility rules
// can be unit-tested without mounting the screen (redux, SVG assets, native
// modules). Same pattern as the sibling getDbsForFilters.js.

import _ from "lodash";
import { formatPackingTripLabel } from "@screens/PartnerRoutingAndPacking/utils";

// A card whose orders are already out for delivery. `ofd:` is the legacy
// null-trip OFD row, equally locked.
export const isTripCard = (routeData) => {
  const cardId = routeData?.trip_card_id;
  if (typeof cardId !== "string") return false;
  return cardId.startsWith("trip:") || cardId.startsWith("ofd:");
};

// Multi-trip CP signal with no API change: the touchpoints response stamps a
// `trip_card_id` (pending:/trip:/ofd:) on EVERY card when the CP has
// multi-trip ON, and on none when OFF (server expandTouchpointsIntoCards
// returns the list untouched when OFF). Used to pick transfer copy only.
export const isMultiTripCp = (touchpoints) =>
  Array.isArray(touchpoints) &&
  touchpoints.some((tp) => typeof tp?.trip_card_id === "string");

/**
 * B-9 — a card whose orders were already presented at the customer today
 * (citymall-services #28906). "Presented" is wider than delivered: any doorstep
 * key counts, so a cancel-at-door or CX_RTO visit is finished work too.
 *
 * The backend decides this, exactly as it decides Unassign: it sends
 * `disabled:{cx}:{route}` with `enable_unassign: false`, and the FE only draws
 * what it is told. The Unassign BUTTON already disappears on its own, because
 * index.js renders it generically off `enable_unassign === true` — this helper
 * exists for the ROUTE CHIPS, which have no such generic gate and would
 * otherwise render a full, tappable chip row on a card representing work that
 * is already done.
 */
export const isDisabledCard = (routeData) => {
  const cardId = routeData?.trip_card_id;
  if (typeof cardId !== "string") return false;
  return cardId.startsWith("disabled:");
};

/**
 * A trip the boy has finished: every order on it presented at the door today
 * (citymall-services, `disabled:trip:{tripId}`). It is a disabled card, so no
 * Unassign and no re-routing, but it still shows which trip it was and the
 * route that trip ran on (owner, 2026-09-21: "show trip tag and its route").
 */
export const isFinishedTripCard = (routeData) => {
  const cardId = routeData?.trip_card_id;
  if (typeof cardId !== "string") return false;
  return cardId.startsWith("disabled:trip:");
};

/**
 * B-1 / C-1 — stable identity per touchpoint card.
 *
 * The backend mints a TRIP card id as `trip:${tripId}`, which carries NO
 * customer — unlike its siblings `pending:${cxId}:${routeId}` and
 * `disabled:${cxId}:${routeId}`, which do. And a trip spans many customers:
 * one Mark-OFD submit mints ONE trip_id for everyone ticked (staging trip
 * 3136bb95-98a4-4206-beb1-75fd6bf336b9 carries 2 distinct cx).
 *
 * So keying the draft map on trip_card_id alone made two customers on one trip
 * share a single slot. Symptoms, all from that one key:
 *   - tapping the SAME route on customer B deleted customer A's staged draft
 *     (the `existing.route_id === routeId` toggle in onPressRoutePill), and a
 *     different route silently overwrote it;
 *   - duplicate React keys out of the touchpoints keyExtractor;
 *   - `Object.values(draftRoutes)` collapsed N customers into ONE `tps` entry
 *     in the submit payload, so only one of them was actually re-routed;
 *   - the CTA count under-reported the number of staged changes.
 *
 * The key therefore carries the customer as well as the card. `|` is a safe
 * separator: card ids are `<kind>:<uuid|int>[:<routeId>]` and never contain it.
 *
 * The legacy (no trip_card_id) branch is left byte-identical, so a flag-OFF or
 * old-backend response behaves exactly as it does today.
 */
export const cardDraftKey = (routeData) => {
  if (routeData?.trip_card_id) {
    return `${routeData.trip_card_id}|${routeData?.cx_id ?? ""}`;
  }
  return `${routeData?.delivery_date}_${routeData?.cx_id}`;
};

/**
 * B-5 — the trip label shown on a locked trip card.
 *
 * Owner, 2026-09-04: "yes, like the trip id we currently show on the OFD
 * page." So this reuses the already-exported formatPackingTripLabel
 * (`Trip · …<last6>`) instead of adding a third copy of the slice rule, and
 * falls back to the OFD screen's own "Live trip" wording for a legacy `ofd:`
 * card that carries no trip id at all.
 *
 * `trip_id` IS available on this screen: getTouchpoints.js:188 spreads
 * cardResponseFields(tp), which copies `trip_id` whenever the OFD row had one.
 * `unassign_trip_id` (stamped by stampUnassignHints) is the fallback.
 */
export const tripCardLabel = (routeData) => {
  if (!isTripCard(routeData) && !isFinishedTripCard(routeData)) return null;
  const tripId = routeData?.trip_id ?? routeData?.unassign_trip_id;
  if (tripId == null || tripId === "") return "Live trip";
  return formatPackingTripLabel(tripId);
};

export const getRouteListSorted = (routesList, routeData, split) => {
  // B-2 / B-4 (owner, 2026-09-04: "yes") — a LOCKED trip card renders ONLY the
  // chip for the route the trip is actually on. Every other chip there is a
  // dead control: onPressRoutePill drops the tap. The one exception was the
  // greyed UNASSIGN chip, which that guard deliberately exempted and which
  // therefore still fired — that IS the B-2 defect. Hiding the whole
  // non-selected set removes both problems at once, and the red Unassign
  // button rendered under the chips already covers the UNASSIGN job.
  // B-9 — a DISABLED card renders NO chips at all, not even its own route.
  // A locked trip card keeps one chip because the trip is still live work the
  // leader may want to locate; a disabled card is closed-out work, and every
  // chip on it would be a control that changes nothing. Checked before
  // isTripCard because a `disabled:` id matches neither branch's prefix test
  // and would otherwise fall through to the full pending chip row.
  // A finished trip keeps the locked single chip of its own route, like the
  // live trip card it was; every other disabled card still renders none.
  if (isDisabledCard(routeData) && !isFinishedTripCard(routeData)) return [];

  if (isTripCard(routeData) || isFinishedTripCard(routeData)) {
    return split === 1
      ? (routesList || []).filter(
          (route) => route.route_id === routeData?.route_id,
        )
      : [];
  }

  let copyRoutesList = _.cloneDeep(routesList);

  // B-6 (owner, 2026-09-04: "we dont need 2 unassign buttons") — a card that
  // draws its own red Unassign button must not ALSO offer the UNASSIGN chip.
  // They are literally the same code path: the button calls onPressRoutePill
  // with routeId "UNASSIGN" and force:true, the chip calls it with the same
  // routeId and no force, and `force` is a no-op on a pending card. The other
  // route chips stay — they are how a customer is moved to another route.
  //
  // A card with NO button (flag-OFF / legacy, where enable_unassign is
  // undefined) keeps the chip: there it is the only way to unassign, so this
  // is what keeps flag-OFF behaviour unchanged.
  //
  // CX_QR_PAYMENT_IN_PROGRESS: a card whose button is greyed because a boy is
  // taking a QR payment (`unassign_blocked_reason`) still draws the button, so
  // the chip stays hidden too — otherwise it would be a second, live way to
  // unassign the customer the button just blocked.
  const hasUnassignButton =
    routeData?.enable_unassign === true || !!routeData?.unassign_blocked_reason;

  const misrouteAndUnassign = copyRoutesList.filter(
    (route) =>
      route.route_id === "MISROUTE" ||
      (route.route_id === "UNASSIGN" &&
        routeData?.route_id &&
        !hasUnassignButton),
  );
  copyRoutesList = copyRoutesList.filter(
    (route) => route.route_id !== "UNASSIGN" && route.route_id !== "MISROUTE",
  );

  const sortedList = copyRoutesList.sort((a, b) => {
    if (a.route_id === routeData.route_id) return -1;
    if (b.route_id === routeData.route_id) return 1;
    if (a.route_id === "UNASSIGN") return 1;
    if (b.route_id === "UNASSIGN") return -1;
    if (a.route_id === "MISROUTE") return 1;
    if (b.route_id === "MISROUTE") return -1;
    return 0;
  });

  if (split === 3) return misrouteAndUnassign;

  if (split === 1 && routesList.length <= 6) return sortedList;

  return split === 1
    ? sortedList.slice(0, Math.ceil(sortedList.length / 2))
    : sortedList.slice(Math.ceil(sortedList.length / 2));
};
